
import React from 'react';
import { Award, Zap, BarChart, Settings, User, Lightbulb, Puzzle } from "lucide-react";

const SkillsTab = () => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium flex items-center">
        <Award className="mr-2 h-5 w-5" /> Skills Acquisition
      </h3>
      <p>
        Skills represent your growing expertise in different areas of the mining industry.
        They provide permanent improvements to your KPIs and unlock new career opportunities.
      </p>
      
      <div className="border rounded-md p-4 bg-muted/20">
        <h4 className="font-medium mb-2">Skill Categories</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="border border-primary/20 rounded p-3">
            <h5 className="font-medium flex items-center">
              <Zap className="h-4 w-4 mr-2 text-blue-400" />
              Technical Skills
            </h5>
            <p>Improve efficiency and uptime of mining operations</p>
            <p className="text-xs mt-1 text-muted-foreground">Examples: Cooling Optimization, Firmware Tuning</p>
            <div className="mt-2 text-xs">
              <p><span className="font-medium">Primary boost</span>: +2-5% Efficiency, +1-3% Uptime</p>
            </div>
          </div>
          <div className="border border-primary/20 rounded p-3">
            <h5 className="font-medium flex items-center">
              <BarChart className="h-4 w-4 mr-2 text-green-400" />
              Business Skills
            </h5>
            <p>Improve profit margins and financial performance</p>
            <p className="text-xs mt-1 text-muted-foreground">Examples: Financial Planning, Hedging Strategies</p>
            <div className="mt-2 text-xs">
              <p><span className="font-medium">Primary boost</span>: +2-5% Profit Margin</p>
            </div>
          </div>
          <div className="border border-primary/20 rounded p-3">
            <h5 className="font-medium flex items-center">
              <Settings className="h-4 w-4 mr-2 text-amber-400" />
              Operations Skills
            </h5>
            <p>Reduce energy costs and improve overall efficiency</p>
            <p className="text-xs mt-1 text-muted-foreground">Examples: Facility Management, Renewable Energy</p>
            <div className="mt-2 text-xs">
              <p><span className="font-medium">Primary boost</span>: -0.01-0.02 Energy Cost, +1-3% Efficiency</p>
            </div>
          </div>
          <div className="border border-primary/20 rounded p-3">
            <h5 className="font-medium flex items-center">
              <User className="h-4 w-4 mr-2 text-purple-400" />
              Leadership Skills
            </h5>
            <p>Improve profit margins and uptime through team management</p>
            <p className="text-xs mt-1 text-muted-foreground">Examples: Team Management, Strategic Planning</p>
            <div className="mt-2 text-xs">
              <p><span className="font-medium">Primary boost</span>: +1-3% Profit Margin, +1-2% Uptime</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="border-l-4 border-primary p-4 bg-primary/5">
        <h4 className="font-medium flex items-center">
          <Lightbulb className="mr-2 h-4 w-4 text-primary" /> How to Get Skills
        </h4>
        <p className="text-sm">
          Skills are primarily acquired by selecting specific options during challenges.
          Each skill has a category that influences which KPIs it improves. Choose
          challenges and options that align with your desired skill development!
        </p>
      </div>
      
      <div className="p-4 border rounded-md bg-muted/10">
        <h4 className="font-medium flex items-center mb-2">
          <Puzzle className="mr-2 h-4 w-4 text-web3-green" /> Skill Synergies
        </h4>
        <p className="text-sm mb-2">
          Certain combinations of skills unlock additional bonuses or special career paths:
        </p>
        <ul className="space-y-2 text-sm list-disc list-inside">
          <li>Technical + Operations skills unlock advanced cooling efficiency techniques</li>
          <li>Business + Leadership skills improve team performance during market downturns</li>
          <li>Technical + Business skills enhance hardware ROI calculations</li>
          <li>Operations + Leadership skills optimize facility staff performance</li>
        </ul>
      </div>
    </div>
  );
};

export default SkillsTab;
